// import
import {
  addDays,
  addMilliseconds,
  addMonths,
  addWeeks,
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInSeconds,
  differenceInWeeks,
  isFuture,
  isPast,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import i18n from '@/i18n/index';
import store from '@/store/shared/modules/current';
import { de, el, enGB, es, fr, it, nl, pt } from 'date-fns/locale';
import { TIMEZONE_DEFAULT } from '@/app/data/model_constants';

/**
 * This is our custom wrapper for the Date Fns library.
 *
 * (Custom) functions, time stamp formats, locales are defined here.
 * If you need a function from Date Fns, import it through this wrapper first.
 */

// constants
const locales = { de, el, en: enGB, es, fr, it, nl, pt };
const DATE_FORMATS = Object.freeze({
  // The prefix 'F' refers to 'Full', meaning the value is written as a full word
  DAY_FMONTH: 'd MMMM', // 31 January
  DAY_FMONTH_YEAR: 'd MMMM yyyy', // 2nd February 2018
  DAY_MONTH_YEAR: 'dd/MM/yyyy', // 02/11/2018
  FDAY_DAY_FMONTH: 'EEEE d MMMM', // Monday 2 December
  FDAY_DAY_FMONTH_YEAR: 'EEEE d MMMM yyyy', // Monday 2 December 2018
  FMONTH_DAY_YEAR: 'MMMM d, yyyy', // January 31, 2018
  FMONTH_YEAR: 'MMMM yyyy', // January 2017
  FULL_DATE: 'do MMMM yyyy, HH:mm', // 2nd January 2018, 24:12
  TIME: 'HH:mm', // '23:12'
  LOCALE_DATE_TIME: 'Pp', // locale-aware date + time
  YEAR_MONTH_DAY: 'yyyy-MM-dd', // 2018-11-01
  YEAR_MONTH_DAY_TIME: 'd MMMM yyyy, HH:mm', // 2018-11-01, 24:12
});

// German writes the day of the month as an ordinal, so `d MMMM` has to render 31. Januar.
// Keyed on the English format string so callers keep passing DATE_FORMATS unchanged.
const FORMAT_OVERRIDES = Object.freeze({
  de: {
    'd MMMM': 'd. MMMM',
    'd MMMM yyyy': 'd. MMMM yyyy',
    'EEEE d MMMM': 'EEEE, d. MMMM',
    'EEEE d MMMM yyyy': 'EEEE, d. MMMM yyyy',
    'MMMM d, yyyy': 'd. MMMM yyyy',
    'do MMMM yyyy, HH:mm': 'd. MMMM yyyy, HH:mm',
    'd MMMM yyyy, HH:mm': 'd. MMMM yyyy, HH:mm',
  },
});

// functions
function parseStringToISO(value) {
  return typeof value === 'string' ? parseISO(value) : value;
}

function formatWithLocale(date, formatStr, { show_time = true, timestamp = false } = {}) {
  if (!date || date === 'Invalid Date') return null;
  let dateObj = date;
  if (typeof date === 'string') dateObj = timestamp ? new Date(date) : parseISO(date);
  const { timezone: timeZone = TIMEZONE_DEFAULT, language = 'en' } = store.state.current_profile.attributes;
  const zonedDate = timestamp ? toZonedTime(dateObj, timeZone) : dateObj;
  if (!show_time && isToday(zonedDate)) return i18n.global.t('timestamp.labels.today');
  if (!show_time && isTomorrow(zonedDate)) return i18n.global.t('timestamp.labels.tomorrow');
  if (!show_time && isYesterday(zonedDate)) return i18n.global.t('timestamp.labels.yesterday');
  const format_options = { locale: locales[language] };
  if (timestamp) format_options.timeZone = timeZone;
  const format_string = (FORMAT_OVERRIDES[language] || {})[formatStr] || formatStr;
  return format(zonedDate, format_string, format_options);
}

function addMillisecondsISO(date, amount) {
  return addMilliseconds(parseStringToISO(date), amount);
}

function isPastISO(date) {
  return isPast(parseStringToISO(date));
}

function distanceInWordsToNowWithLocale(date_utc = null) {
  if (!date_utc) return null;
  if (date_utc && date_utc !== 'Invalid Date') {
    const now = new Date();
    const then = new Date(date_utc);
    const difference_seconds_to_now = differenceInSeconds(now, then);
    if (difference_seconds_to_now <= 60 && difference_seconds_to_now >= -60)
      return i18n.global.t('timestamp.labels.justNow');
    const { timezone: timeZone = TIMEZONE_DEFAULT } = store.state.current_profile.attributes;
    const then_zoned = toZonedTime(then, timeZone);
    if (isToday(then_zoned)) return i18n.global.t('timestamp.labels.today');
    if (isTomorrow(then_zoned)) return i18n.global.t('timestamp.labels.tomorrow');
    if (isYesterday(then_zoned)) return i18n.global.t('timestamp.labels.yesterday');
    return formatWithLocale(date_utc, DATE_FORMATS.DAY_MONTH_YEAR, { timestamp: true });
  }
  return null;
}

function getDaysRemaining(date_utc = null) {
  if (!date_utc) return null;
  if (date_utc && date_utc !== 'Invalid Date') {
    const now = new Date();
    const then = new Date(date_utc);
    const difference_days_to_now = differenceInDays(then, now);
    if (difference_days_to_now < 0) {
      return i18n.global.t('timestamp.labels.overdue', { days: Math.abs(difference_days_to_now) });
    }
    if (difference_days_to_now === 0) {
      return i18n.global.t('timestamp.labels.dueToday');
    }
    if (difference_days_to_now > 0) {
      return i18n.global.t('timestamp.labels.daysRemaining', { days: difference_days_to_now });
    }
  }
  return null;
}

function getFullDate(date_utc = null) {
  return formatWithLocale(date_utc, DATE_FORMATS.FULL_DATE, { timestamp: true });
}

function getDateWithTime(date_utc = null) {
  return formatWithLocale(date_utc, DATE_FORMATS.YEAR_MONTH_DAY_TIME, { timestamp: true });
}

function getSentOnTimestamp(date_utc = null) {
  if (!date_utc || date_utc === 'Invalid Date') return null;
  const date_value = formatWithLocale(date_utc, DATE_FORMATS.YEAR_MONTH_DAY, { timestamp: true });
  const time_value = formatWithLocale(date_utc, DATE_FORMATS.TIME, { timestamp: true });
  if (!date_value || !time_value) return null;
  return i18n.global.t('timestamp.labels.sentOnAt', { date: date_value, time: time_value });
}

function getRecency(date_utc) {
  if (date_utc && date_utc !== 'Invalid Date') {
    const now = new Date();
    const then = new Date(date_utc);
    const difference_weeks_to_now = differenceInWeeks(now, then);

    if (difference_weeks_to_now < 1) {
      return `${differenceInDays(now, then)}_days`;
    } else if (difference_weeks_to_now < 4) {
      return `${difference_weeks_to_now}_weeks`;
    } else if (difference_weeks_to_now < 52) {
      return `${Math.floor(difference_weeks_to_now / 4)}_months`;
    } else if (difference_weeks_to_now >= 52) {
      return `${Math.floor(difference_weeks_to_now / 52)}_years`;
    }

    return null;
  }
  return null;
}

function getResponseTime(date_utc_primary, date_utc_secondary) {
  if (
    (date_utc_secondary && date_utc_secondary !== 'Invalid Date') ||
    (date_utc_primary && date_utc_primary !== 'Invalid Date')
  ) {
    const start = new Date(date_utc_primary);
    const then = new Date(date_utc_secondary);
    if (then < start) return null;
    const difference_days = differenceInDays(then, start);

    if (difference_days < 1) {
      const difference_hours = differenceInHours(then, start);
      if (difference_hours < 1) {
        const difference_minutes = differenceInMinutes(then, start);
        return i18n.global.t(
          'timestamp.labels.minutesAgo',
          { minutes: difference_minutes },
          (difference_minutes > 1) + 1,
        );
      }
      return i18n.global.t('timestamp.labels.hoursAgo', { hours: difference_hours }, (difference_hours > 1) + 1);
    } else if (difference_days < 7) {
      return i18n.global.t('timestamp.labels.daysAgo', { days: difference_days }, (difference_days > 1) + 1);
    } else if (difference_days < 30) {
      const weeks = Math.floor(difference_days / 7);
      return i18n.global.t('timestamp.labels.weeksAgo', { weeks: weeks }, (weeks > 1) + 1);
    } else if (difference_days < 365) {
      const months = Math.floor(difference_days / 30);
      return i18n.global.t('timestamp.labels.monthsAgo', { months: months }, (months > 1) + 1);
    } else if (difference_days >= 365) {
      const years = Math.floor(difference_days / 365);
      return i18n.global.t('timestamp.labels.yearsAgo', { years: years }, (years > 1) + 1);
    }

    return null;
  }
  return null;
}

function getTimeBasedGreeting() {
  const { timezone: timeZone = TIMEZONE_DEFAULT } = store.state.current_profile.attributes;
  const now = toZonedTime(new Date(), timeZone);
  const hour = now.getHours();

  // Define time periods
  if (hour >= 5 && hour < 12) {
    return 'morning';
  }
  if (hour >= 12 && hour < 17) {
    return 'afternoon';
  }
  if (hour >= 17 && hour < 22) {
    return 'evening';
  }
  return 'night';
}

// export
export {
  addDays,
  addMonths,
  addWeeks,
  addMillisecondsISO,
  getDaysRemaining,
  DATE_FORMATS,
  differenceInDays,
  differenceInMilliseconds,
  distanceInWordsToNowWithLocale,
  format,
  formatWithLocale,
  getFullDate,
  getDateWithTime,
  getSentOnTimestamp,
  getRecency,
  getResponseTime,
  getTimeBasedGreeting,
  isPastISO,
  isFuture,
  subDays,
  subMonths,
  subWeeks,
};
